import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import {
  isMessagePopupType,
  isOfferPopupType,
  isOfferV2PopupType,
  isRollingOfferPopupType,
} from '../../../../utils/events';
import { CoinBackWidget } from '../../../CoinBack/CoinBackWidget';
import { DailyMissionWidget } from '../../../DailyMissions/components/DailyMissionWidget';
import { ChallengesWidget } from '../../../Dialogs/ChallengesDialog/ChallengesWidget';
import { MetagamesWidget } from '../../../Dialogs/MetagamesDialog/MetagamesWidget';
import { MonetizationEventWidget } from '../../../Dialogs/MonetizationEventsDialog/MonetizationEventsWidget';
import QuestWidget from '../../../Quest/components/QuestWidget.tsx/QuestWidget';
import { UserFreeSpinsWidget } from '../../../UserFreeSpins/UserFreeSpinsWidget';
import { UserSnappyGiftsWidget } from '../../../UserSnappyGifts/UserFreeSpinsWidget';
import TournamentWidget from '../../TournamentWidget/TournamentWidget';
import { EventTriggerWidget } from '../component/EventTriggerWidget';
import { MAX_HOME_WIDGETS } from '../constants';
import { sortHomeWidgets } from '../helpers';
import type { HomeWidget, HomeWidgetsPriority } from '../types';

const useHomeWidgets = () => {
  const {
    eventTriggerStore,
    tournamentStore,
    dailyMissionsStore,
    coinBackStore,
    questStore,
    userFreeSpinsStore,
    hammersStore,
    prizeDropStore,
    puzzleStore,
    userSnappyGiftsStore,
    trailStore,
    plinkoStore,
    rouletteStore,
    challengesStore,
    sportEventsStore,
  } = useStores();

  const homeWidgetsOrder = useFlag<HomeWidgetsPriority | undefined>('home_widgets_order', undefined);
  const userFsWidgetsFF = useFlag('user_free_spins_widgets_feature', true);
  const dailyMissionsFF = useFlag('daily_missions_feature', true);
  const metagamesFF = useFlag('metagames_enabled', true);
  const offersFF = useFlag('offers_feat', true);
  const messagesFF = useFlag('messages_feat', true);
  const roadFF = useFlag('road_feature_web', false);
  const challengesFF = useFlag('challenges_feat', false);
  const monetizationEventsFF = useFlag('monetization_events_enabled', true);

  const { hasLeaderboard } = tournamentStore;
  const { currentDailyMission } = dailyMissionsStore;
  const { currentCoinBack, currentCoinBackBooster, isTutorialOpen: isCoinBackTutorialOpen } = coinBackStore;
  const { questsFlag, currentPlayerQuest } = questStore;
  const { widgetEventsLists } = eventTriggerStore;
  const { userFreeSpinsData } = userFreeSpinsStore;
  const { hammersEvent } = hammersStore;
  const { prizeDropEvent } = prizeDropStore;
  const { puzzleEvent } = puzzleStore;
  const { userSnappyGiftsData } = userSnappyGiftsStore;
  const { showWidget: showCoinBackWidget = false } = currentCoinBack ?? {};
  const { trailEvent } = trailStore;
  const { plinkoEvent } = plinkoStore;
  const { rouletteEvent } = rouletteStore;
  const { challenges } = challengesStore;
  const { sportEvent } = sportEventsStore;
  const widgets = useMemo(() => {
    const messages = widgetEventsLists.filter((event) => isMessagePopupType(event.benefit));
    const offers = widgetEventsLists.filter(
      (event) =>
        isOfferPopupType(event.benefit) || isOfferV2PopupType(event.benefit) || isRollingOfferPopupType(event.benefit),
    );

    const roads = widgetEventsLists.filter((event) => event.benefit.benefitType === 'road');

    const showCoinBack = Boolean(
      currentCoinBack && (showCoinBackWidget || isCoinBackTutorialOpen || currentCoinBackBooster),
    );
    const showUpcomingDailyMission = Boolean(dailyMissionsFF && currentDailyMission?.status === 'UPCOMING');
    const showUserFreeSpins = Boolean(userFsWidgetsFF && !isEmpty(userFreeSpinsData));
    const showUserSnappyGifts = Boolean(!isEmpty(userSnappyGiftsData));
    const showTournament = hasLeaderboard;
    const showOffers = Boolean(offersFF && offers.length > 0);
    const showMessages = Boolean(messagesFF && messages.length > 0);
    const showQuests = Boolean(questsFlag && currentPlayerQuest);
    const showMetagames = Boolean(metagamesFF);
    const showChallenges = Boolean(challengesFF && challenges.length > 0);
    const showMonetizationEvents = Boolean(monetizationEventsFF);

    const metagamesEvents = [
      ...(hammersEvent ? [hammersEvent] : []),
      ...(prizeDropEvent ? [prizeDropEvent] : []),
      ...(puzzleEvent ? [puzzleEvent] : []),
      ...(trailEvent ? [trailEvent] : []),
      ...(plinkoEvent ? [plinkoEvent] : []),
      ...(rouletteEvent ? [rouletteEvent] : []),
    ];

    const monetizationEvents = [...(sportEvent ? [sportEvent] : [])];

    const widgetsList: HomeWidget[] = [
      ...metagamesEvents
        .filter((ev) => !ev.isCrownSlotOnly)
        .map((event) => ({
          key: event.id,
          type: 'MetaGameEvent' as const,
          Component: MetagamesWidget,
          props: { event },
          isVisible: showMetagames,
          dataTestId: `metagames-${event.typeName}-widget`,
        })),
      ...monetizationEvents.map((event) => ({
        key: event.id,
        type: 'MonetizationEvent' as const,
        Component: MonetizationEventWidget,
        props: { event },
        isVisible: showMonetizationEvents,
        dataTestId: `monetization-event-${event.gameNameType}-${event.id}-widget`,
      })),
      {
        key: 'challenges',
        type: 'Challenge' as const,
        Component: ChallengesWidget,
        isVisible: showChallenges,
        dataTestId: 'challenge-widget',
      },
      {
        key: 'tournament',
        type: 'Tournament',
        Component: () => <TournamentWidget />,
        isVisible: showTournament,
        dataTestId: 'tournament-widget',
      },
      {
        key: 'coinback',
        type: 'Coinback',
        Component: () => <CoinBackWidget />,
        isVisible: showCoinBack,
        dataTestId: 'coinback-widget',
      },
      {
        key: 'daily-mission',
        type: 'UpcomingDailyMission',
        Component: () => (
          <div className="daily-mission-widget-wrapper">
            <DailyMissionWidget />
          </div>
        ),
        isVisible: showUpcomingDailyMission,
        dataTestId: 'daily-mission-widget',
      },
      {
        key: 'quest',
        type: 'Quest',
        Component: QuestWidget,
        isVisible: showQuests,
        dataTestId: 'quest-widget',
      },
      ...userFreeSpinsData.map((freeSpinData) => ({
        key: freeSpinData.id,
        type: 'UnclaimedFreeSpin' as const,
        Component: UserFreeSpinsWidget,
        props: { freeSpinData },
        isVisible: showUserFreeSpins,
        dataTestId: 'user-free-spins-widget',
      })),
      ...offers.map((offer) => ({
        key: offer.userEventTriggerId,
        type: 'Offer' as const,
        Component: EventTriggerWidget,
        props: { event: offer },
        isVisible: showOffers,
        weight: offer.eventTrigger.weight,
        dataTestId: 'offer-widget',
      })),
      ...messages.map((message) => ({
        key: message.userEventTriggerId,
        type: 'Message' as const,
        Component: EventTriggerWidget,
        props: { event: message },
        isVisible: showMessages,
        weight: message.eventTrigger.weight,
        dataTestId: 'message-widget',
      })),
      ...userSnappyGiftsData.map((snappyGiftData) => ({
        key: snappyGiftData.id,
        type: 'SnappyGift' as const,
        Component: UserSnappyGiftsWidget,
        props: { snappyGiftData },
        isVisible: showUserSnappyGifts,
        dataTestId: 'user-snappy-gifts-widget',
      })),
      ...roads.map((road) => {
        const roadWidget = {
          key: road.userEventTriggerId,
          type: 'CrownRoad' as const,
          Component: EventTriggerWidget,
          props: { event: road },
          isVisible: roadFF,
          weight: road.eventTrigger.weight,
          dataTestId: 'road-widget',
        };
        return roadWidget;
      }),
    ];

    const filteredWidgets = widgetsList.filter((widget) => widget.isVisible);
    const orderedWidgets = sortHomeWidgets(filteredWidgets, homeWidgetsOrder);
    const widgetsByLimit = orderedWidgets.slice(0, MAX_HOME_WIDGETS);

    return widgetsByLimit;
  }, [
    widgetEventsLists,
    currentCoinBack,
    showCoinBackWidget,
    isCoinBackTutorialOpen,
    currentCoinBackBooster,
    dailyMissionsFF,
    currentDailyMission?.status,
    userFsWidgetsFF,
    userFreeSpinsData,
    userSnappyGiftsData,
    hasLeaderboard,
    offersFF,
    messagesFF,
    questsFlag,
    currentPlayerQuest,
    metagamesFF,
    challengesFF,
    challenges.length,
    monetizationEventsFF,
    hammersEvent,
    prizeDropEvent,
    puzzleEvent,
    trailEvent,
    plinkoEvent,
    rouletteEvent,
    sportEvent,
    homeWidgetsOrder,
    roadFF,
  ]);

  return { widgets };
};

export default useHomeWidgets;
